import { useCallback } from 'react';
import { useIntl } from 'react-intl';

type Values = Record<string, string | number>;

const useFmText = () => {
    const intl = useIntl();

    const t = useCallback(
        (key: string, values?: Values) => intl.formatMessage({ id: `intl-msg:fleetMonitoring.${key}` }, values),
        [intl]
    );

    const formatNumber = useCallback((value: number) => value.toLocaleString(intl.locale), [intl.locale]);

    const formatDate = useCallback(
        (value?: Date | string) => (value ? new Date(value).toLocaleDateString(intl.locale) : '–'),
        [intl.locale]
    );

    const vehicleType = useCallback(
        (type?: string) => {
            if (!type) {
                return '';
            }
            const id = `intl-msg:fleetMonitoring.vehicleType.${type}`;
            return intl.messages[id] ? intl.formatMessage({ id }) : type;
        },
        [intl]
    );

    const kmToNext = useCallback(
        (km?: number) => {
            if (km === undefined || km === null) {
                return undefined;
            }
            return km < 0
                ? t('table.kmOverdue', { km: formatNumber(Math.round(-km)) })
                : t('table.kmRemaining', { km: formatNumber(Math.round(km)) });
        },
        [t, formatNumber]
    );

    const updatedAgo = useCallback(
        (days: number) => (days <= 1 ? t('table.updatedToday') : t('table.updatedDaysAgo', { days })),
        [t]
    );

    const formatMetric = useCallback(
        (metric: { value: number } | undefined, unit: string) =>
            metric ? `${formatNumber(Math.round(metric.value))} ${unit}` : '–',
        [formatNumber]
    );

    return { t, formatNumber, formatDate, vehicleType, kmToNext, updatedAgo, formatMetric };
};

export default useFmText;
